import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Card, CardContent, Chip,
    IconButton, Tabs, Tab, InputAdornment, Avatar, AvatarGroup,
    Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
    Select, MenuItem, OutlinedInput, Checkbox, ListItemText,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, ToggleButton, ToggleButtonGroup, Autocomplete,
    Menu, CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PersonIcon from '@mui/icons-material/Person';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

function Tickets() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createLoading, setCreateLoading] = useState(false);
    const [filter, setFilter] = useState('all'); // all, assigned_me, unassigned, closed

    // Keep track of filter for async operations
    const filterRef = React.useRef(filter);
    useEffect(() => { filterRef.current = filter; }, [filter]);

    const [search, setSearch] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [open, setOpen] = useState(false);
    
    const token = localStorage.getItem('token');
    // Parse userId immediately for default state
    const storedUserId = localStorage.getItem('user_id');
    const initialAssignedToIds = storedUserId ? [parseInt(storedUserId)] : [];

    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [itProjects, setItProjects] = useState([]);
    const [types, setTypes] = useState([]);
    const [countries, setCountries] = useState([]);
    const [newTicket, setNewTicket] = useState({
        title: '',
        description: '',
        status: 'Open',
        priority: 'Medium',
        requested_by: '',
        sender_email: '',
        project_id: '',
        it_project_id: '',
        ticket_type_id: '',
        country_id: '',
        assigned_to_ids: initialAssignedToIds,
        due_date: ''
    });
    const navigate = useNavigate();
    const location = useLocation();

    const userId = storedUserId;
    const headers = { Authorization: `Bearer ${token}` };

    const [assignedToMe, setAssignToMe] = useState(true);
    const [selectedCountries, setSelectedCountries] = useState([]);

    // Status Menu State
    const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
    const [statusMenuTicketId, setStatusMenuTicketId] = useState(null);

    // AD Search State
    const [adUsers, setAdUsers] = useState([]);
    const [adLoading, setAdLoading] = useState(false);

    const handleADSearch = async (query) => {
        if (!query || query.length < 3) {
            setAdUsers([]);
            return;
        }
        setAdLoading(true);
        try {
            const res = await axios.get(`${API_URL}/tickets/search-ad-users?q=${query}`, { headers });
            setAdUsers(res.data);
        } catch (e) {
            console.error(e);
        } finally {
            setAdLoading(false);
        }
    };

    const handleStatusClick = (event, ticketId) => {
        event.stopPropagation();
        setStatusMenuAnchor(event.currentTarget);
        setStatusMenuTicketId(ticketId);
    };

    const handleStatusClose = () => {
        setStatusMenuAnchor(null);
        setStatusMenuTicketId(null);
    };

    const handleStatusUpdate = async (newStatus) => {
        if (!statusMenuTicketId) return;
        try {
            await axios.put(`${API_URL}/tickets/${statusMenuTicketId}`, { status: newStatus }, { headers });
            fetchTickets();
        } catch (e) { console.error(e); }
        handleStatusClose();
    };

    const getCurrentUser = () => {
        if (!token) return null;
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            return null;
        }
    };
    const currentUser = getCurrentUser();

    // Ref to track if sync has already started (prevents double sync in StrictMode)
    const syncStartedStr = React.useRef(false);

    const fetchTickets = async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            // 2. Fetch updated tickets
            const currentFilter = filterRef.current;
            const params = { user_id: userId, filter: currentFilter };
            const res = await axios.get(`${API_URL}/tickets`, { headers, params });
            setTickets(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const syncEmails = async () => {
        try {
            if (syncStartedStr.current) return;
            syncStartedStr.current = true;

            await axios.post(`${API_URL}/tickets/sync-emails`, {}, { headers });
            // After sync, refresh tickets to show any new ones
            fetchTickets();
        } catch (err) {
            console.error("Sync failed", err);
        }
    };

    const fetchMeta = async () => {
        try {
            const [usersRes, projectsRes, itProjectsRes, typesRes, countriesRes] = await Promise.all([
                axios.get(`${API_URL}/team`, { headers }),
                axios.get(`${API_URL}/projects`, { headers }),
                axios.get(`${API_URL}/it-projects`, { headers }),
                axios.get(`${API_URL}/ticket-types`, { headers }),
                axios.get(`${API_URL}/setup/countries`, { headers })
            ]);
            setUsers(usersRes.data);
            setProjects(projectsRes.data);
            setItProjects(itProjectsRes.data);
            setTypes(typesRes.data);
            setCountries(countriesRes.data);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchTickets();
        fetchMeta();

        // Refresh tickets every 30 seconds to catch new emails from the background script
        const interval = setInterval(() => {
            fetchTickets(false); // Silent refresh
        }, 30000);

        return () => clearInterval(interval);
    }, [filter]);



    useEffect(() => {
        if (location.state && location.state.createFromEmail) {
            const { title, description, requested_by, sender_email } = location.state.createFromEmail;

            // Convert HTML body to plain text if possible, or just use it.
            // For simplicity, we just use the raw body (which might be HTML)
            // If the backend expects plain text, we might want to strip tags here or in InboxDetails

            // Create a temporary element to strip HTML tags for the description preview if needed
            let plainTextDescription = description;
            try {
                const tempDiv = document.createElement("div");
                tempDiv.innerHTML = description;
                plainTextDescription = tempDiv.textContent || tempDiv.innerText || "";
            } catch (e) {
                console.log("Error parsing HTML description", e);
            }

            setNewTicket(prev => ({
                ...prev,
                title: title || '',
                description: plainTextDescription || '',
                requested_by: requested_by || '',
                sender_email: sender_email || ''
            }));
            setOpen(true);

            // Clear state to prevent reopening on refresh (requires navigating)
            // But doing navigate replace here might trigger re-render loops if not careful.
            // For now, we leave it or we can replace history.
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleCreate = async () => {
        setCreateLoading(true);
        try {
            const payload = { ...newTicket, created_by_id: userId };
            await axios.post(`${API_URL}/tickets`, payload, { headers });
            setOpen(false);
            fetchTickets();
            setNewTicket({
                title: '', description: '', status: 'Open', priority: 'Medium',
                requested_by: '', sender_email: '', project_id: '', it_project_id: '', ticket_type_id: '',
                assigned_to_ids: initialAssignedToIds, due_date: ''
            });
        } catch (err) {
            alert('Error creating ticket');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setFilter(newValue);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Done': return 'success';
            case 'In Progress': return 'info';
            case 'Open': return 'warning';
            case 'Reopen': return 'error';
            default: return 'default';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Critical': return 'error';
            case 'High': return 'warning';
            case 'Medium': return 'info';
            case 'Low': return 'success';
            default: return 'default';
        }
    };

    const filteredTickets = tickets.filter(t => {
        const matchesSearch = t.title.toLowerCase().includes(search.toLowerCase()) ||
            (t.type && t.type.toLowerCase().includes(search.toLowerCase())) ||
            (String(t.id).includes(search));

        const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(t.country_name);

        // For assignments, t.assigned_to is now array of objects {id, full_name, username}
        const matchesAssign = !assignedToMe || (t.assigned_to && t.assigned_to.some(u => u.id === currentUser?.user_id || u.id === parseInt(userId)));

        // Status filter is handled by backend + local chips?
        // Current implementation uses 'filter' state for backend fetch.
        // But we also want local filtering if possible or just use the local chips to set the backend filter?
        // The user wants 'Status Chips' like ITProjects.
        // ITProjects uses local filtering on status. Tickets uses backend.
        // We will stick to backend fetching for 'closed' vs 'active', but handle sub-status locally if needed
        // For 'filter' state: 'all' (Active), 'closed' (Done).

        // Actually, let's keep the filter simple:
        // The status chips will update the 'filter' state which triggers fetch.

        return matchesSearch && matchesCountry && matchesAssign;
    });

    return (
        <Box>
            {/* Header Toolbar */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>

                    {/* Left: Filters */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                        <Chip
                            icon={<PersonIcon />}
                            label="My Tickets"
                            onClick={() => setAssignToMe(!assignedToMe)}
                            color={assignedToMe ? "primary" : "default"}
                            variant={assignedToMe ? "filled" : "outlined"}
                            sx={{ fontWeight: 'bold' }}
                        />
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                                multiple
                                value={selectedCountries}
                                onChange={(e) => setSelectedCountries(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                displayEmpty
                                variant="outlined"
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return <Typography color="textSecondary" sx={{ fontSize: '0.875rem' }}>All Countries</Typography>;
                                    }
                                    return selected.join(', ');
                                }}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem disabled value="">
                                    <Typography variant="body2">Select Countries</Typography>
                                </MenuItem>
                                {countries.map(c => (
                                    <MenuItem key={c.id} value={c.name}>
                                        <Checkbox checked={selectedCountries.indexOf(c.name) > -1} size="small" />
                                        {c.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Right: Actions */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <TextField
                            placeholder="Search tickets..."
                            size="small"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                                sx: { borderRadius: 5, bgcolor: 'grey.50' }
                            }}
                            sx={{ width: { xs: '100%', sm: 250 } }}
                        />

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                            size="small"
                            sx={{ borderRadius: 2 }}
                        >
                            <ToggleButton value="grid" aria-label="grid view">
                                <ViewModuleIcon />
                            </ToggleButton>
                            <ToggleButton value="list" aria-label="list view">
                                <ViewListIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setOpen(true)}
                            sx={{ borderRadius: 5, whiteSpace: 'nowrap', textTransform: 'none', px: 3 }}
                        >
                            New Ticket
                        </Button>
                    </Box>
                </Box>

                {/* Status Tabs (mapped to backend filter) */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                    {[
                        { label: 'Active', value: 'all' },
                        { label: 'Unassigned', value: 'unassigned' },
                        { label: 'Closed', value: 'closed' }
                    ].map(f => (
                        <Chip
                            key={f.value}
                            label={f.label}
                            onClick={() => setFilter(f.value)}
                            clickable
                            color={filter === f.value ? 'primary' : 'default'}
                            variant={filter === f.value ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 500 }}
                        />
                    ))}
                </Box>
            </Paper>

            {loading ? <Loading /> : (
                viewMode === 'grid' ? (
                    <Grid container spacing={3}>
                        {filteredTickets.length === 0 ? (
                            <Grid item xs={12}>
                                <Typography align="center" sx={{ py: 3, color: 'text.secondary' }}>No tickets found</Typography>
                            </Grid>
                        ) : filteredTickets.map((t) => (
                            <Grid item xs={12} md={4} lg={3} key={t.id}>
                                <Card
                                    sx={{ height: '100%', borderRadius: 3, boxShadow: 1, cursor: 'pointer', transition: '0.3s', '&:hover': { boxShadow: 4 } }}
                                    onClick={() => navigate(`/tickets/${t.id}`)}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Box sx={{ overflow: 'hidden', mr: 1 }}>
                                                <Typography variant="caption" color="primary" fontWeight="bold" sx={{ display: 'block', mb: 0.5 }}>
                                                    {`ACTS - T - ${String(t.id).padStart(5, '0')}`}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</Typography>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>{t.type}</Typography>
                                                {t.requested_by && <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Req: {t.requested_by}</Typography>}
                                            </Box>
                                            <Chip
                                                label={t.status}
                                                size="small"
                                                color={getStatusColor(t.status)}
                                                onClick={(e) => handleStatusClick(e, t.id)}
                                                sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0, cursor: 'pointer' }}
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                <Typography variant="caption" color="textSecondary">{t.created_at}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" color="textSecondary">Due: {t.due_date || 'N/A'}</Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(t.priority) }} />
                                                <Typography variant="caption" color="textSecondary">{t.priority || 'Medium'}</Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #eee' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" color="textSecondary">By: {t.created_by}</Typography>
                                            </Box>

                                            {t.assigned_to && t.assigned_to.length > 0 ? (
                                                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                                                    {t.assigned_to.map((u, i) => (
                                                        <Avatar key={u.id || i} alt={u.full_name} src="/static/images/avatar/1.jpg" title={u.full_name}>
                                                            {u.full_name ? u.full_name.charAt(0).toUpperCase() : '?'}
                                                        </Avatar>
                                                    ))}
                                                </AvatarGroup>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                        <Table sx={{ minWidth: 650 }} aria-label="tickets table">
                            <TableHead sx={{ bgcolor: 'grey.50' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Assigned To</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Due Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredTickets.map((t) => (
                                    <TableRow
                                        key={t.id}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' } }}
                                        onClick={() => navigate(`/tickets/${t.id}`)}
                                    >
                                        <TableCell component="th" scope="row">
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                {`ACTS - T - ${String(t.id).padStart(5, '0')}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">{t.title}</Typography>
                                            {t.requested_by && <Typography variant="caption" color="textSecondary">Req: {t.requested_by}</Typography>}
                                        </TableCell>
                                        <TableCell>{t.type}</TableCell>
                                        <TableCell>{t.country_name || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={t.status}
                                                size="small"
                                                color={getStatusColor(t.status)}
                                                variant="outlined"
                                                onClick={(e) => handleStatusClick(e, t.id)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getPriorityColor(t.priority) }} />
                                                {t.priority || 'Medium'}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {t.assigned_to && t.assigned_to.length > 0 ? (
                                                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 12 } }}>
                                                    {t.assigned_to.map((u, i) => (
                                                        <Avatar key={u.id || i} alt={u.full_name} src="/static/images/avatar/1.jpg" title={u.full_name}>
                                                            {u.full_name ? u.full_name.charAt(0).toUpperCase() : '?'}
                                                        </Avatar>
                                                    ))}
                                                </AvatarGroup>
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">Unassigned</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">{t.created_at}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{t.due_date || 'N/A'}</Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredTickets.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body2" color="textSecondary">No tickets found matching your criteria.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            )}

            <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={handleStatusClose}
            >
                {['Open', 'In Progress', 'Reopen', 'Done'].map(s => (
                    <MenuItem key={s} onClick={() => handleStatusUpdate(s)}>{s}</MenuItem>
                ))}
            </Menu>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Create New Ticket</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Title / Subject"
                            fullWidth
                            value={newTicket.title}
                            onChange={e => setNewTicket({ ...newTicket, title: e.target.value })}
                            required
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={newTicket.ticket_type_id}
                                    label="Type"
                                    onChange={e => setNewTicket({ ...newTicket, ticket_type_id: e.target.value })}
                                >
                                    {types.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    value={newTicket.priority}
                                    label="Priority"
                                    onChange={e => setNewTicket({ ...newTicket, priority: e.target.value })}
                                >
                                    <MenuItem value="Low">Low</MenuItem>
                                    <MenuItem value="Medium">Medium</MenuItem>
                                    <MenuItem value="High">High</MenuItem>
                                    <MenuItem value="Critical">Critical</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Country</InputLabel>
                                <Select
                                    value={newTicket.country_id}
                                    label="Country"
                                    onChange={e => setNewTicket({ ...newTicket, country_id: e.target.value })}
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    {countries.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>

                            <Autocomplete
                                freeSolo
                                fullWidth
                                options={adUsers}
                                loading={adLoading}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.displayName || option.name || '';
                                }}
                                onInputChange={(event, newInputValue) => {
                                    handleADSearch(newInputValue);
                                }}
                                renderOption={(props, option) => (
                                    <li {...props}>
                                        <Box>
                                            <Typography variant="body1">{option.displayName || option.name}</Typography>
                                            {option.mail && <Typography variant="caption" color="textSecondary">{option.mail}</Typography>}
                                            {option.country && <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>({option.country})</Typography>}
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Requested By (Azure AD)"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {adLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                                value={newTicket.requested_by}
                                onChange={async (event, newValue) => {
                                    if (typeof newValue === 'string') {
                                        setNewTicket({ ...newTicket, requested_by: newValue });
                                        // Try to fetch from Azure AD if email-like
                                        if (newValue.includes('@')) {
                                            try {
                                                const res = await axios.get(`${API_URL}/tickets/check-ad-user?email=${newValue}`, { headers });
                                                if (res.data.found && res.data.country_id) {
                                                    setNewTicket(prev => ({ ...prev, country_id: res.data.country_id }));
                                                }
                                            } catch (e) { console.log("AD Check failed", e); }
                                        }
                                    } else if (newValue) {
                                        const name = newValue.displayName || newValue.name;
                                        const email = newValue.mail || newValue.sender_email;

                                        let updates = { ...newTicket, requested_by: name, sender_email: email || newTicket.sender_email };

                                        // If AD user has country
                                        if (newValue.country) {
                                            const matchedCountry = countries.find(c => c.name === newValue.country);
                                            if (matchedCountry) {
                                                updates.country_id = matchedCountry.id;
                                            }
                                        }
                                        // If local user has countries
                                        else if (newValue.countries && newValue.countries.length > 0) {
                                            const cName = newValue.countries[0];
                                            const cObj = countries.find(c => c.name === cName);
                                            if (cObj) {
                                                updates.country_id = cObj.id;
                                            }
                                        }

                                        setNewTicket(updates);
                                    } else {
                                        setNewTicket({ ...newTicket, requested_by: '' });
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                label="Sender Email"
                                value={newTicket.sender_email}
                                onChange={e => setNewTicket({ ...newTicket, sender_email: e.target.value })}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Autocomplete
                                fullWidth
                                options={projects}
                                getOptionLabel={(option) => option.name || ''}
                                value={projects.find(p => p.id === newTicket.project_id) || null}
                                onChange={(event, newValue) => {
                                    setNewTicket({
                                        ...newTicket,
                                        project_id: newValue ? newValue.id : '',
                                        it_project_id: '' // Clear IT Project if regular project is selected
                                    });
                                }}
                                renderInput={(params) => <TextField {...params} label="Related Project (Client)" />}
                            />
                            <Autocomplete
                                fullWidth
                                options={itProjects}
                                getOptionLabel={(option) => option.name || ''}
                                value={itProjects.find(p => p.id === newTicket.it_project_id) || null}
                                onChange={(event, newValue) => {
                                    setNewTicket({
                                        ...newTicket,
                                        it_project_id: newValue ? newValue.id : '',
                                        project_id: '' // Clear regular project if IT project is selected
                                    });
                                }}
                                renderInput={(params) => <TextField {...params} label="Related IT Project" />}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Due Date"
                                value={newTicket.due_date}
                                onChange={e => setNewTicket({ ...newTicket, due_date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel>Assign To (Team)</InputLabel>
                            <Select
                                multiple
                                value={newTicket.assigned_to_ids}
                                onChange={(e) => {
                                    const { target: { value } } = e;
                                    setNewTicket({
                                        ...newTicket,
                                        assigned_to_ids: typeof value === 'string' ? value.split(',') : value,
                                    });
                                }}
                                input={<OutlinedInput label="Assign To (Team)" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const user = users.find(u => u.id === value);
                                            return <Chip key={value} label={user ? user.name : value} size="small" />;
                                        })}
                                    </Box>
                                )}
                                MenuProps={MenuProps}
                            >
                                {users.map((user) => (
                                    <MenuItem key={user.id} value={user.id}>
                                        <Checkbox checked={newTicket.assigned_to_ids.indexOf(user.id) > -1} />
                                        <ListItemText primary={user.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Description"
                            value={newTicket.description}
                            onChange={e => setNewTicket({ ...newTicket, description: e.target.value })}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
                        {createLoading ? 'Creating...' : 'Create Ticket'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Tickets;
